import React, { FC } from "react";

import { startOfWeek, endOfWeek } from "date-fns";

import { formatDateTime } from "~/libs";

import {
  useDateRange,
  useMultiDivisionMultiSectionDropdown,
  useDivisionSectionDropdown,
  useProvidersCurrentEmployee,
  useApisManagersWeeklyReportsCumulativeAnalysisIndex,
  useApisManagersWeeklyReportsPostsRateCalendarsIndex,
  useApisManagersWeeklyReportsFeedbackAnalysisIndex,
  useApisManagersWeeklyReportsEmployeeSurveyProgressesIndex,
  useApisManagersWeeklyReportsEmployeeConditionSurveyProgressesIndex,
  usePrevNextSelectMonth,
} from "~/hooks";

import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
} from "~/components/layouts";
import {
  AnalysisCumulativeOfPostGraph,
  DivisionAndSectionAndEmployeeDropdownField,
  ParentWithChildDropdownField,
  ProgressTableForSurveyProgress,
} from "~/components/organisms";

import { CatchTitleBorder } from "~/ui";
import {
  RangeDatePicker,
  DoughnutChartBox,
  ActivityOriginalCalendar,
} from "~/ui";

const today = new Date();

export const ManagersWeeklyReportsIndex: FC = () => {
  const { currentEmployee } = useProvidersCurrentEmployee();

  const breadCrumbItems = [
    { label: "週次レポート", href: "/managers/weekly_reports" },
    { label: "一覧", href: "/managers/weekly_reports" },
  ];

  const {
    selectableDivisions,
    selectedDivisions,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSections,
    onSectionChange,
    selectableEmployees,
    selectedEmployee,
    onEmployeeChange,
  } = useMultiDivisionMultiSectionDropdown({
    selectableDivisions: currentEmployee?.businessDivisions,
    selectableSections: currentEmployee?.businessSections,
    isSelectEmployee: true,
  });

  const {
    selectableDivisions: progressSelectableDivisions,
    selectedDivision: progressSelectedDivision,
    onDivisionChange: progressOnDivisionChange,
    optionSelectableSections: progressOptionSelectableSections,
    optionSelectedSection: progressOptionSelectedSection,
    onSectionChange: progressOnSectionChange,
  } = useDivisionSectionDropdown({
    selectableDivisions: currentEmployee?.businessDivisions || [],
    selectableSections: currentEmployee?.businessSections || [],
  });

  const [
    {
      startDate,
      formattedStartDate,
      endDate,
      formattedEndDate,
      onChange: onChangeDateRange,
    },
  ] = useDateRange([startOfWeek(today), endOfWeek(today)]);

  const {
    targetDate: calendarTargetDate,
    targetDateString: calendarTargetDateString,
    handlePrevMonthClick,
    handleNextMonthClick,
  } = usePrevNextSelectMonth();

  const commonParams = {
    businessDivisionIds: selectedDivisions.map((division) => division.id),
    businessSectionIds: optionSelectedSections.map((section) => section.id),
    employeeIds: selectedEmployee ? [selectedEmployee.id] : undefined,
  };

  const { data: cumulativeData, isLoading: isCumulativeLoading } =
    useApisManagersWeeklyReportsCumulativeAnalysisIndex({
      params: {
        ...commonParams,
        fromDate: formattedStartDate || undefined,
        toDate: formattedEndDate || undefined,
      },
      config: {
        enabled: !!(formattedStartDate && formattedEndDate),
      },
    });

  const { data: calendarData, isLoading: isCalendarLoading } =
    useApisManagersWeeklyReportsPostsRateCalendarsIndex({
      params: {
        ...commonParams,
        targetDate: calendarTargetDateString,
      },
      config: {
        enabled: !!calendarTargetDateString,
      },
    });

  const { data: feedbackData, isLoading: isFeedbackLoading } =
    useApisManagersWeeklyReportsFeedbackAnalysisIndex({
      params: {
        ...commonParams,
        fromDate: formattedStartDate || undefined,
        toDate: formattedEndDate || undefined,
      },
      config: {
        enabled: !!(formattedStartDate && formattedEndDate),
      },
    });

  const { data: progressesData } =
    useApisManagersWeeklyReportsEmployeeSurveyProgressesIndex({
      params: {
        businessSectionId: progressOptionSelectedSection?.id || "",
        startDate: formattedStartDate || undefined,
        endDate: formattedEndDate || undefined,
      },
      config: {
        enabled: !!(
          formattedStartDate &&
          formattedEndDate &&
          progressOptionSelectedSection
        ),
      },
    });

  const { data: conditionProgressesData } =
    useApisManagersWeeklyReportsEmployeeConditionSurveyProgressesIndex({
      params: {
        businessSectionId: progressOptionSelectedSection?.id || "",
        startDate: formattedStartDate || undefined,
        endDate: formattedEndDate || undefined,
      },
      config: {
        enabled: !!(
          formattedStartDate &&
          formattedEndDate &&
          progressOptionSelectedSection
        ),
      },
    });

  return (
    <EmployeeLayout withRightSidebar={false} breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent withRightSidebar={false}>
        <div className="space-y-12">
          <div className="grid miniTablet:grid-cols-2 gap-4">
            <DivisionAndSectionAndEmployeeDropdownField
              selectableDropdownDivisions={selectableDivisions}
              selectedDropdownDivisions={selectedDivisions}
              onDivisionChange={onDivisionChange}
              selectableDropdownSections={optionSelectableSections}
              selectedDropdownSections={optionSelectedSections}
              onSectionChange={onSectionChange}
              selectableDropdownEmployees={selectableEmployees}
              selectedDropdownEmployee={selectedEmployee}
              onEmployeeChange={onEmployeeChange}
            />
            <RangeDatePicker
              labelText="期間"
              startDate={startDate}
              endDate={endDate}
              onChange={onChangeDateRange}
              maxDate={today}
            />
          </div>

          <div className="space-y-6">
            <CatchTitleBorder text="累計分析" />
            <div className="grid miniTablet:grid-cols-2 desktop:grid-cols-3 gap-6">
              <AnalysisCumulativeOfPostGraph
                title="メモログ"
                isLoading={isCumulativeLoading}
                postAnalysis={cumulativeData?.insightPostsDailyPostsCounts.map(
                  (item) => ({
                    ...item,
                    name: formatDateTime(item.name, "MM-dd"),
                  }),
                )}
              />
              <AnalysisCumulativeOfPostGraph
                title="チェックアウト"
                isLoading={isCumulativeLoading}
                postAnalysis={cumulativeData?.employeeSurveySubmissionsDailyPostsCounts.map(
                  (item) => ({
                    ...item,
                    name: formatDateTime(item.name, "MM-dd"),
                  }),
                )}
              />
              <AnalysisCumulativeOfPostGraph
                title="コンディション"
                isLoading={isCumulativeLoading}
                postAnalysis={cumulativeData?.employeeConditionSubmissionsDailyPostsCounts.map(
                  (item) => ({
                    ...item,
                    name: formatDateTime(item.name, "MM-dd"),
                  }),
                )}
              />
            </div>
          </div>

          <div className="space-y-6">
            <CatchTitleBorder text="フィードバック分析" />
            <div className="grid miniTablet:grid-cols-2 desktop:grid-cols-3 gap-6">
              <DoughnutChartBox
                title="メモログへのいいね率"
                doughnut={feedbackData?.insightPostLikesDoughnut}
                isLoading={isFeedbackLoading}
              />
              <DoughnutChartBox
                title="メモログへのリプライ率"
                doughnut={feedbackData?.insightPostCommentsDoughnut}
                isLoading={isFeedbackLoading}
              />
              <DoughnutChartBox
                title="チェックアウトへのいいね率"
                doughnut={feedbackData?.employeeSurveySubmissionLikesDoughnut}
                isLoading={isFeedbackLoading}
              />
              <DoughnutChartBox
                title="チェックアウトへのリプライ率"
                doughnut={feedbackData?.employeeSurveySubmissionRepliesDoughnut}
                isLoading={isFeedbackLoading}
              />
              <DoughnutChartBox
                title="コンディションへのいいね率"
                doughnut={
                  feedbackData?.employeeConditionSubmissionLikesDoughnut
                }
                isLoading={isFeedbackLoading}
              />
              <DoughnutChartBox
                title="コンディションへのリプライ率"
                doughnut={
                  feedbackData?.employeeConditionSubmissionRepliesDoughnut
                }
                isLoading={isFeedbackLoading}
              />
            </div>
          </div>

          <div className="space-y-6">
            <CatchTitleBorder text="実施カレンダー" />
            <div className="grid miniTablet:grid-cols-2 desktop:grid-cols-3 gap-6">
              <div>
                <h3 className="text-base font-semibold mb-3">
                  メモログ実施カレンダー
                </h3>
                <ActivityOriginalCalendar
                  isLoading={isCalendarLoading}
                  dailyActivity={calendarData?.insightPostsCalendar}
                  targetDate={calendarTargetDate}
                  onPrevMonthClick={handlePrevMonthClick}
                  onNextMonthClick={handleNextMonthClick}
                  isForRate={false}
                />
              </div>
              <div>
                <h3 className="text-base font-semibold mb-3">
                  チェックアウト実施カレンダー
                </h3>
                <ActivityOriginalCalendar
                  isLoading={isCalendarLoading}
                  dailyActivity={
                    calendarData?.employeeSurveySubmissionsCalendar
                  }
                  targetDate={calendarTargetDate}
                  onPrevMonthClick={handlePrevMonthClick}
                  onNextMonthClick={handleNextMonthClick}
                  isForRate={false}
                />
              </div>
              <div>
                <h3 className="text-base font-semibold mb-3">
                  コンディション実施カレンダー
                </h3>
                <ActivityOriginalCalendar
                  isLoading={isCalendarLoading}
                  dailyActivity={
                    calendarData?.employeeConditionSubmissionsCalendar
                  }
                  targetDate={calendarTargetDate}
                  onPrevMonthClick={handlePrevMonthClick}
                  onNextMonthClick={handleNextMonthClick}
                  isForRate={false}
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <CatchTitleBorder text="進捗確認" />
            <ParentWithChildDropdownField
              allParents={progressSelectableDivisions}
              parentValue={progressSelectedDivision}
              parentsOnChange={progressOnDivisionChange}
              parentRequired={true}
              parentLabel="配布先: 部署"
              allChildren={progressOptionSelectableSections}
              childValue={progressOptionSelectedSection}
              childrenOnChange={progressOnSectionChange}
              childRequired={true}
              childLabel="配布先: 課"
            />

            {progressOptionSelectedSection && (
              <>
                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4 text-primary-700">
                    チェックアウト進捗
                  </h3>
                  {progressesData?.surveyProgresses.map((surveyProgress) => (
                    <div
                      key={surveyProgress.employeeSurvey.id}
                      className="mt-6"
                    >
                      <h4 className="text-base font-semibold mb-4 text-secondary-700">
                        {surveyProgress.employeeSurvey.title}
                        <span className="text-sm text-secondary-500 ml-2">
                          ({surveyProgress.employeeSurvey.startDate} 〜{" "}
                          {surveyProgress.employeeSurvey.endDate})
                        </span>
                      </h4>
                      <ProgressTableForSurveyProgress
                        startDate={startDate}
                        endDate={endDate}
                        userProgress={surveyProgress.userProgress}
                      />
                    </div>
                  ))}
                </div>

                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4 text-primary-700">
                    コンディションアンケート進捗
                  </h3>
                  {conditionProgressesData?.surveyProgresses.map(
                    (surveyProgress) => (
                      <div
                        key={surveyProgress.employeeConditionSurvey.id}
                        className="mt-6"
                      >
                        <h4 className="text-base font-semibold mb-4 text-secondary-700">
                          {surveyProgress.employeeConditionSurvey.title}
                          <span className="text-sm text-secondary-500 ml-2">
                            ({surveyProgress.employeeConditionSurvey.startDate}{" "}
                            〜 {surveyProgress.employeeConditionSurvey.endDate})
                          </span>
                        </h4>
                        <ProgressTableForSurveyProgress
                          startDate={startDate}
                          endDate={endDate}
                          userProgress={surveyProgress.userProgress.map(
                            (user) => ({
                              employee: {
                                id: user.employeeId,
                                name: user.employeeName,
                                avatarUrl: null,
                              },
                              progress: user.progress,
                            }),
                          )}
                        />
                      </div>
                    ),
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </EmployeeLayoutMainContent>
    </EmployeeLayout>
  );
};
